import React, { useState, useEffect } from 'react';
import {
  Card,
  Space,
  Typography,
  Tag,
  Table,
  Button,
  InputNumber,
  Input,
  Modal,
  Form,
  message,
  Spin,
  Progress,
  Row,
  Col,
  Statistic,
  Empty,
} from 'antd';
import {
  TrophyOutlined,
  StarOutlined,
  HistoryOutlined,
  PlusOutlined,
  SafetyCertificateOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import { getTokens } from '../../services/authApi';
import { useAuth } from '../../contexts/AuthContext';

const { Text } = Typography;

interface CustomerRewardsTabProps {
  customerId: number;
}

interface RewardBalance {
  customerId: number;
  points: number;
  tier: string;
  lifetimePointsEarned: number;
  nextTier: string | null;
  pointsToNextTier: number | null;
}

interface RewardTransaction {
  id: number;
  customer_id: number;
  type: string;
  points: number;
  balance_after: number;
  description: string | null;
  created_at: string;
}

interface HeroInfo {
  category: string | null;
  verified: boolean;
  verifiedAt: string | null;
}

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

const getAuthHeaders = () => {
  const tokens = getTokens();
  return tokens?.accessToken ? { Authorization: `Bearer ${tokens.accessToken}` } : {};
};

const tierColors: Record<string, string> = {
  bronze: '#cd7f32',
  silver: '#c0c0c0',
  gold: '#ffd700',
  platinum: '#4169e1',
};

const typeColors: Record<string, string> = {
  earned: 'green',
  redeemed: 'red',
  bonus: 'blue',
  referral: 'purple',
  expired: 'default',
};

const CustomerRewardsTab: React.FC<CustomerRewardsTabProps> = ({ customerId }) => {
  const [balance, setBalance] = useState<RewardBalance | null>(null);
  const [transactions, setTransactions] = useState<RewardTransaction[]>([]);
  const [totalTx, setTotalTx] = useState(0);
  const [hero, setHero] = useState<HeroInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [adjustModalVisible, setAdjustModalVisible] = useState(false);
  const [adjusting, setAdjusting] = useState(false);
  const [adjustForm] = Form.useForm();
  const { user } = useAuth();
  const isOwner = user?.role === 'owner';

  const fetchRewards = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/admin/rewards/customer/${customerId}`, {
        headers: getAuthHeaders(),
      });
      setBalance(response.data.balance);
      setTransactions(response.data.history?.transactions || []);
      setTotalTx(response.data.history?.total || 0);
      setHero(response.data.hero);
    } catch (error) {
      message.error('Failed to load reward data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRewards();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId]);

  const handleAdjust = async (values: { points: number; reason: string }) => {
    try {
      setAdjusting(true);
      await axios.post(
        `${API_URL}/admin/rewards/adjust`,
        { customerId, points: values.points, reason: values.reason },
        { headers: getAuthHeaders() }
      );
      message.success('Points adjusted successfully');
      setAdjustModalVisible(false);
      adjustForm.resetFields();
      fetchRewards();
    } catch (error: any) {
      message.error(error.response?.data?.error || 'Failed to adjust points');
    } finally {
      setAdjusting(false);
    }
  };

  const handleVerifyHero = async () => {
    try {
      await axios.post(
        `${API_URL}/admin/rewards/verify-hero`,
        { customerId },
        { headers: getAuthHeaders() }
      );
      message.success('Hero status verified');
      fetchRewards();
    } catch (error: any) {
      message.error(error.response?.data?.error || 'Failed to verify hero');
    }
  };

  const columns = [
    {
      title: 'Date',
      dataIndex: 'created_at',
      key: 'date',
      width: 160,
      render: (date: string) => new Date(date).toLocaleDateString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit',
      }),
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      width: 100,
      render: (type: string) => (
        <Tag color={typeColors[type] || 'default'}>{type.toUpperCase()}</Tag>
      ),
    },
    {
      title: 'Points',
      dataIndex: 'points',
      key: 'points',
      width: 100,
      render: (points: number) => (
        <Text strong style={{ color: points >= 0 ? '#52c41a' : '#ff4d4f' }}>
          {points >= 0 ? '+' : ''}{points}
        </Text>
      ),
    },
    {
      title: 'Balance After',
      dataIndex: 'balance_after',
      key: 'balance',
      width: 120,
      render: (bal: number) => <Text>{bal.toLocaleString()}</Text>,
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
    },
  ];

  if (loading) {
    return <div style={{ textAlign: 'center', padding: 32 }}><Spin size="large" /></div>;
  }

  if (!balance) {
    return <Empty description="No reward data available" />;
  }

  const tierProgress = balance.pointsToNextTier !== null && balance.nextTier
    ? ((balance.lifetimePointsEarned / (balance.lifetimePointsEarned + balance.pointsToNextTier)) * 100)
    : 100;

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      {/* Balance Overview */}
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="Current Points"
              value={balance.points}
              prefix={<StarOutlined style={{ color: '#faad14' }} />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="Reward Tier"
              value={balance.tier.charAt(0).toUpperCase() + balance.tier.slice(1)}
              prefix={<TrophyOutlined style={{ color: tierColors[balance.tier] || '#999' }} />}
              valueStyle={{ color: tierColors[balance.tier] || '#999' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="Lifetime Points Earned"
              value={balance.lifetimePointsEarned}
              prefix={<HistoryOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {/* Tier Progress */}
      {balance.nextTier && balance.pointsToNextTier !== null && (
        <Card size="small">
          <Text type="secondary">
            Progress to {balance.nextTier.charAt(0).toUpperCase() + balance.nextTier.slice(1)} tier
          </Text>
          <Progress
            percent={Math.round(tierProgress)}
            status="active"
            strokeColor={tierColors[balance.nextTier]}
          />
          <Text type="secondary" style={{ fontSize: 12 }}>
            {balance.pointsToNextTier.toLocaleString()} points to go
          </Text>
        </Card>
      )}

      {/* Hero Status */}
      {hero && hero.category && (
        <Card
          size="small"
          title={<Space><SafetyCertificateOutlined style={{ color: '#eb2f96' }} /> Hero Status</Space>}
        >
          <Space>
            <Tag color="purple">{hero.category}</Tag>
            {hero.verified ? (
              <Tag icon={<CheckCircleOutlined />} color="success">Verified</Tag>
            ) : (
              <Space>
                <Tag color="warning">Pending Verification</Tag>
                {isOwner && (
                  <Button size="small" type="primary" onClick={handleVerifyHero}>
                    Verify Hero
                  </Button>
                )}
              </Space>
            )}
          </Space>
        </Card>
      )}

      {/* Actions */}
      {isOwner && (
        <div>
          <Button
            icon={<PlusOutlined />}
            onClick={() => setAdjustModalVisible(true)}
          >
            Adjust Points
          </Button>
        </div>
      )}

      {/* Transaction History */}
      <Card
        title={`Transaction History (${totalTx})`}
        size="small"
      >
        <Table
          columns={columns}
          dataSource={transactions}
          rowKey="id"
          size="small"
          pagination={{ pageSize: 10 }}
          scroll={{ x: 600 }}
        />
      </Card>

      {/* Adjust Points Modal */}
      <Modal
        title="Adjust Customer Points"
        open={adjustModalVisible}
        onCancel={() => { setAdjustModalVisible(false); adjustForm.resetFields(); }}
        onOk={() => adjustForm.submit()}
        confirmLoading={adjusting}
      >
        <Form form={adjustForm} layout="vertical" onFinish={handleAdjust}>
          <Form.Item
            name="points"
            label="Points (positive to add, negative to subtract)"
            rules={[{ required: true, message: 'Enter point amount' }]}
          >
            <InputNumber style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            name="reason"
            label="Reason"
            rules={[{ required: true, min: 3, message: 'Enter a reason (min 3 chars)' }]}
          >
            <Input.TextArea rows={3} placeholder="e.g., Customer loyalty bonus, correction, etc." />
          </Form.Item>
        </Form>
      </Modal>
    </Space>
  );
};

export default CustomerRewardsTab;
